import clsx from 'clsx';
import { CHASSIS_SIZE_OPTIONS } from 'data/constants';
import { DuctedFilterSelectionSource } from 'features/ui';

// The CHASSIS SIZE row in the What You Want card (AHA-6796, Geoff
// 2026-08-13): the SYSTEM SIZE static-scale treatment over the SHARED
// cabinetChassisSize combination-filter entry (the same enum the Choose
// Systems faceted spec control edits) — a surfaced control of that one
// filter state, not a second filter. Fully controlled: the host owns the
// selection (read from ductedFilters.filters) and the dispatches this
// emits; the seed itself lands via the shared context reset.
type ChassisSectionProps = Readonly<{
  selected: string[];
  source: DuctedFilterSelectionSource;
  // The equipment-derived seed sizes (exact uploaded cabinetChassisSize
  // union) — the honest-source hint input.
  seedSizes: string[];
  onToggle: (size: string) => void;
}>;

export default function ChassisSection({
  selected,
  source,
  seedSizes,
  onToggle
}: ChassisSectionProps) {
  let hint: React.ReactNode;
  if (selected.length === 0) {
    hint = (
      <span className='text-gray-500'>
        Showing all chassis sizes — pick one to narrow your matches.
      </span>
    );
  } else if (source === 'seed' && seedSizes.length > 0) {
    hint = (
      <span className='text-gray-600'>
        <span className='font-semibold'>From your equipment:</span> chassis size
        {seedSizes.length > 1 ? 's' : ''} {seedSizes.join(', ')}. Adjust freely
        — this is a starting point, not a lock.
      </span>
    );
  } else {
    hint = (
      <span className='text-gray-600'>
        <span className='font-semibold'>Your pick</span>
        <span className='text-gray-500'> — adjust or clear anytime.</span>
      </span>
    );
  }

  return (
    <div className='mt-3' data-testid='chassis-section'>
      <div className='mb-1 flex items-baseline gap-2'>
        <span className='text-[11px] font-semibold uppercase tracking-wider text-gray-500'>
          Chassis size
        </span>
      </div>
      <div className='flex flex-wrap items-center gap-1.5'>
        {CHASSIS_SIZE_OPTIONS.map((size) => {
          const isSelected = selected.includes(size);
          return (
            <button
              key={size}
              type='button'
              aria-pressed={isSelected}
              onClick={() => onToggle(size)}
              className={clsx(
                'w-12 rounded-lg border px-0 py-1.5 text-center text-xs leading-snug',
                isSelected
                  ? 'cursor-pointer border-blue-600 bg-blue-50 font-semibold text-blue-800'
                  : 'cursor-pointer border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              )}
            >
              {size}
            </button>
          );
        })}
      </div>
      <div
        data-testid='chassis-hint'
        className='mt-2 border-l-2 border-blue-300 bg-gray-50 px-2 py-1.5 text-xs'
      >
        {hint}
      </div>
    </div>
  );
}
